'use client';

import { ExpressiveButtonInnerTypes } from './expressive-button-base.types';
import { Icon } from '@exploration/icons';
import { tablet } from '@thrivent/midwest-shared';
import styled, { css } from 'styled-components';

// --------------------------------------------------------------------------

export const ArrowRight = styled(Icon)`
  margin-left: -0.25rem;
  path {
    fill: ${(p) => p.theme.midwestColors.textPrimaryDarkBG};
  }
`;

// --------------------------------------------------------------------------

// size - handles padding & font-size

const bigButtonStyles = css`
  padding: 1.5rem;
  font-size: 1.125rem;
`;
const smallButtonStyles = css`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
`;

const defaultButtonStyles = css`
  padding: 1rem 1.5rem;
  font-size: 1rem;
`;

// --------------------------------------------------------------------------

// Base -- applied to all the buttons regardless of type, variant or size

const baseButtonStyles = css<ExpressiveButtonInnerTypes>`
  min-width: 0;
  font-weight: 700;
  line-height: 1;
  border: 2px solid transparent;
  font-family: ${(p) => p.theme.font.primary};

  && {
    width: 100%;
    display: inline-flex;
  }

  ${tablet} {
    && {
      width: fit-content;
    }
  }

  ${(p) => p.$eSize === 'large' && bigButtonStyles}
  ${(p) => p.$eSize === 'small' && smallButtonStyles}
  ${(p) => p.$eSize === 'default' && defaultButtonStyles}
`;

// --------------------------------------------------------------------------

// Type -- DarkBG, Primary, Secondary, Tertiary
// Just colors being set here (background, color, border-color, icon->fill)

export const ButtonDarkBgStyles = css`
  ${baseButtonStyles}
  background-color: #FBF2E3;
  color: ${(p) => p.theme.midwestColors.textSecondary};

  ${ArrowRight} {
    path {
      fill: ${(p) => p.theme.midwestColors.textSecondary};
    }
  }

  &:hover,
  &:focus {
    background-color: #f6e2c2;
  }

  &:active {
    background-color: #f4ddb6;
  }

  &:focus-visible::after {
    border: 3px solid ${(p) => p.theme.midwestColors.borderFocusDarkBg};
  }
`;

export const ButtonDarkBgGhostStyles = css`
  ${baseButtonStyles}
  background-color: transparent;
  color: ${(p) => p.theme.midwestColors.white};

  ${ArrowRight} {
    path {
      fill: ${(p) => p.theme.midwestColors.white};
    }
  }

  &:hover,
  &:focus {
    border-color: ${(p) => p.theme.midwestColors.white};
    background-color: transparent;
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryPressed};
    color: ${(p) => p.theme.midwestColors.textSecondary};
    border-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryPressed};
    ${ArrowRight} {
      path {
        fill: ${(p) => p.theme.midwestColors.textSecondary};
      }
    }
  }

  &:focus-visible::after {
    border: 3px solid ${(p) => p.theme.midwestColors.borderFocusDarkBg};
  }
`;

export const ButtonPrimaryStyles = css`
  ${baseButtonStyles}
  background-color: ${(p) =>
    p.theme.midwestColors
      .componentButtonColorBackgroundStrongDestructiveDefault};
  color: ${(p) => p.theme.midwestColors.textPrimaryDarkBG};

  &:hover,
  &:focus {
    background-color: ${(p) => p.theme.midwestColors.sentimentNegativeHeavy};
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors
        .componentButtonColorBackgroundStrongDestructivePressed};
  }
`;

export const ButtonPrimaryGhostStyles = css`
  ${baseButtonStyles}
  background-color: transparent;
  color: ${(p) =>
    p.theme.midwestColors
      .componentButtonColorBackgroundStrongDestructiveDefault};
  ${ArrowRight} {
    path {
      fill: ${(p) =>
        p.theme.midwestColors
          .componentButtonColorBackgroundStrongDestructiveDefault};
    }
  }

  &:hover,
  &:focus {
    border-color: ${(p) =>
      p.theme.midwestColors
        .componentButtonColorBackgroundStrongDestructiveDefault};
    background-color: transparent;
    color: ${(p) =>
      p.theme.midwestColors
        .componentButtonColorBackgroundStrongDestructiveDefault};
  }

  &:active {
    background-color: ${(p) => p.theme.midwestColors.backgroundNegativeSubtle};
  }
`;

export const ButtonSecondaryStyles = css`
  ${baseButtonStyles}
  background-color: ${(p) =>
    p.theme.midwestColors.componentButtonColorBackgroundStrongPrimaryDefault};
  color: ${(p) => p.theme.midwestColors.white};

  &:hover,
  &:focus {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundStrongPrimaryHover};
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundStrongPrimaryPressed};
  }
`;

export const ButtonSecondaryGhostStyles = css`
  ${baseButtonStyles}
  background-color: transparent;
  color: ${(p) =>
    p.theme.midwestColors.componentButtonColorBackgroundStrongPrimaryDefault};
  ${ArrowRight} {
    path {
      fill: ${(p) =>
        p.theme.midwestColors
          .componentButtonColorBackgroundStrongPrimaryDefault};
    }
  }

  &:hover,
  &:focus {
    background-color: transparent;
    border-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundStrongPrimaryDefault};
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryPressed};
  }
`;

export const ButtonTertiaryStyles = css`
  ${baseButtonStyles}
  background-color: ${(p) => p.theme.midwestColors.white};
  color: ${(p) => p.theme.midwestColors.textSecondary};
  border-color: ${(p) => p.theme.midwestColors.textSecondary};

  ${ArrowRight} {
    path {
      fill: ${(p) => p.theme.midwestColors.textSecondary};
    }
  }

  &:hover,
  &:focus {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryHover};
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryPressed};
  }
`;

export const ButtonTertiaryGhostStyles = css`
  ${baseButtonStyles}
  background-color: transparent;
  color: ${(p) => p.theme.midwestColors.textSecondary};
  border-color: ${(p) => p.theme.midwestColors.textSecondary};

  ${ArrowRight} {
    path {
      fill: ${(p) => p.theme.midwestColors.textSecondary};
    }
  }

  &:hover,
  &:focus {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryHover};
  }

  &:active {
    background-color: ${(p) =>
      p.theme.midwestColors.componentButtonColorBackgroundSubtlePrimaryPressed};
  }
`;
